/**
 * @fileoverview Rate resolution arithmetic (MOR-1018, MOR-1038)
 * @module timesheets/rateMath
 *
 * Purpose:
 * - Decide what one hour of one person's time on one job is charged at
 * - Keep every rule that touches money in one place that can be tested
 *   without a database
 *
 * The rules, in order:
 *
 * 1. Lookup date. A job may freeze its rates as at a date ("this matter
 *    stays on 2024 rates"). When it does, that date is used for every
 *    lookup below; otherwise the entry's own date is, so time is always
 *    valued at the rate in force when the work was done.
 * 2. Base rate. An individual override in force on the lookup date, else
 *    the person's grade rate in force on the lookup date. No rate on that
 *    date means no rate at all — callers report the units as unrated rather
 *    than guessing at a later one.
 * 3. Card. A job on a multiplier card charges base x multiplier, rounded to
 *    the card's step. The multiplier applies to overrides too: a 25%
 *    discount job discounts everyone on it.
 *
 * Pure by design (no ctx, no db): `shared.buildRateResolver` loads the
 * inputs, this decides what they mean. Same split as `provisionMath.ts`.
 *
 * Used by:
 * - convex/timesheets/shared.ts (buildRateResolver)
 * - convex/timesheets/rateCards.ts (the per-grade preview of a card)
 * - src/components/timesheets/* (live preview while editing a card)
 */

/** A rate that applies from a date onwards, until a later one supersedes it. */
export interface DatedRate {
  hourlyRate: number;
  /** ISO date (YYYY-MM-DD), inclusive. */
  effectiveFrom: string;
}

/** A rate that applies over an interval; open-ended when `effectiveTo` is unset. */
export interface RateOverride extends DatedRate {
  /** ISO date (YYYY-MM-DD), inclusive. */
  effectiveTo?: string;
}

/** The terms of a multiplier card. */
export interface RateCardTerms {
  /** 0.75 = 25% discount, 1.25 = 25% uplift. */
  multiplier: number;
  /** Round to the nearest multiple of this many dollars. Unset = cents. */
  roundTo?: number;
}

export interface RateInputs {
  gradeRates: readonly DatedRate[];
  overrides: readonly RateOverride[];
  /** The job's multiplier card; `null` = Standard. */
  card: RateCardTerms | null;
  /** The job's freeze date, if it has one. */
  ratesAsAt?: string;
}

export interface ComputedRate {
  /** What is charged per hour, after override, card and rounding. */
  hourlyRate: number;
  /** The rate before the card was applied. */
  baseHourlyRate: number;
  /** Whether the base came from the grade table or an individual override. */
  source: "grade" | "override";
  /** The date rates were looked up at: the freeze, else the entry date. */
  lookupDate: string;
}

/** Float noise allowance so 40.5 that arrives as 40.49999999 still rounds up. */
const EPSILON = 1e-9;

/**
 * The rate in force on `date`: the one with the latest `effectiveFrom` on or
 * before it. ISO dates compare correctly as strings.
 */
export function rateInForce<T extends DatedRate>(
  rates: readonly T[],
  date: string,
): T | null {
  return rates.reduce<T | null>((best, rate) => {
    if (rate.effectiveFrom > date) return best;
    return best === null || rate.effectiveFrom > best.effectiveFrom
      ? rate
      : best;
  }, null);
}

/**
 * The override in force on `date`. Each override is an interval, so one that
 * has ended no longer applies — and an earlier, still-open override is back
 * in force once a later, shorter one runs out. Where intervals overlap the
 * one that started most recently wins.
 */
export function overrideInForce<T extends RateOverride>(
  overrides: readonly T[],
  date: string,
): T | null {
  const covering = overrides.filter(
    (o) =>
      o.effectiveFrom <= date &&
      (o.effectiveTo === undefined || o.effectiveTo >= date),
  );
  return covering.reduce<T | null>(
    (best, o) =>
      best === null || o.effectiveFrom > best.effectiveFrom ? o : best,
    null,
  );
}

/** Round to the nearest multiple of `step` dollars (halves up), else to cents. */
export function roundToStep(value: number, step: number | undefined): number {
  const unit = step !== undefined && step > 0 ? step : 0.01;
  const rounded = Math.round(value / unit + EPSILON) * unit;
  // Multiplying back by 0.01 reintroduces binary noise (1234.5600000001).
  return Math.round(rounded * 100) / 100;
}

/** A base rate as charged on a card. Standard (`null`) charges it as is. */
export function applyCard(
  baseHourlyRate: number,
  card: RateCardTerms | null,
): number {
  if (!card) return baseHourlyRate;
  return roundToStep(baseHourlyRate * card.multiplier, card.roundTo);
}

/**
 * What an hour is charged at for work done on `entryDate`, or `null` when
 * neither an override nor a grade rate is in force on the lookup date.
 */
export function computeRate(
  inputs: RateInputs,
  entryDate: string,
): ComputedRate | null {
  const lookupDate = inputs.ratesAsAt ?? entryDate;
  const override = overrideInForce(inputs.overrides, lookupDate);
  const base = override ?? rateInForce(inputs.gradeRates, lookupDate);
  if (!base) return null;
  return {
    hourlyRate: applyCard(base.hourlyRate, inputs.card),
    baseHourlyRate: base.hourlyRate,
    source: override ? "override" : "grade",
    lookupDate,
  };
}
